<script lang="ts">
    import type {Loan} from 'typings/portaro.be.types';
    import type {PageableListState} from 'shared/utils/reactive-pageable-list';
    import type {EmptyObject} from 'typings/portaro.fe.types';
    import type {LoanList, SeekingList, UserAccountReactivePageData, UserAccountStaticPageData} from '../types';
    import type {Subscription} from 'rxjs';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpUserAccountPageService} from '../kp-user-account-page.service';
    import {onDestroy} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {getPageContext} from 'shared/layouts/page-context';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpActionRequestButton from 'shared/components/kp-action-request-button/KpActionRequestButton.svelte';
    import KpVerticalButtonsPanel from 'shared/ui-widgets/vertical-buttons-panel/KpVerticalButtonsPanel.svelte';

    const localize = getLocalization();
    const service = getInjector().getByToken<KpUserAccountPageService>(KpUserAccountPageService.serviceName);
    const pageContext = getPageContext<UserAccountStaticPageData, UserAccountReactivePageData>();

    const user = pageContext.staticData.user;
    const detailView = pageContext.staticData.detailView;

    let loanList: LoanList | undefined;
    let waitingLoanList: LoanList | undefined;
    let endedLoanList: LoanList | undefined;
    let seekingsOfRequester: SeekingList | undefined;

    const reactiveDataUnsubscribe = pageContext.reactiveData.subscribe((data) => {
        loanList = data.loanList;
        waitingLoanList = data.waitingLoanList;
        endedLoanList = data.endedLoanList;
        seekingsOfRequester = data.seekingsOfRequester;
    });

    let loanListSubscription: Subscription;
    let loanListState: PageableListState<Loan, string, EmptyObject>;

    $: if (exists(loanList) && !exists(loanListSubscription)) {
        loanListSubscription = loanList.getState$().subscribe((value) => loanListState = value);
    }

    onDestroy(() => {
        reactiveDataUnsubscribe();

        if (exists(loanListSubscription)) {
            loanListSubscription.unsubscribe();
        }
    });
</script>

<KpVerticalButtonsPanel>
    <span slot="heading">{localize(/* @kp-localization konto.LoansManagement */ 'konto.LoansManagement')}</span>

    <!-- MVS -->
    {#if exists(seekingsOfRequester) && detailView.canSeekingRequestCreate}
        <KpButton isBlock
                  dataQa="send-mvs-request-button"
                  on:click={() => service.createSeekingRequest(user, seekingsOfRequester)}>

            {localize(/* @kp-localization ill.seeking.SendIllRequest */ 'ill.seeking.SendIllRequest')}
        </KpButton>
    {/if}

    <!-- Renew all -->
    {#if exists(loanListState) && detailView.renewAllShowing}
        <KpButton isBlock
                  dataQa="renew-all-button"
                  isDisabled="{!service.enableRenewAllButton(loanListState)}"
                  on:click={() => service.renewAllLoans(user, loanList)}>

            {localize(/* @kp-localization loan.RenewAllAtOnce */ 'loan.RenewAllAtOnce')}
        </KpButton>
    {/if}

    <!-- Lend loan -->
    {#if exists(loanList) && exists(waitingLoanList) && detailView.canLoanLend}
        <KpButton isBlock
                  dataQa="rent-book-button"
                  on:click={() => service.showLendingForm(user, loanList, waitingLoanList)}>

            {localize(/* @kp-localization loan.VypujcitKnihu */ 'loan.VypujcitKnihu')}
        </KpButton>
    {/if}

    <!-- Return -->
    {#if exists(loanList) && exists(endedLoanList) && exists(loanListState) && detailView.canLoanReturnAny}
        <KpButton isBlock
                  dataQa="return-book-button"
                  isDisabled="{!service.enableReturnButton(loanListState)}"
                  on:click={() => service.showReturningForm(user, loanList, endedLoanList)}>

            {localize(/* @kp-localization loan.VratitKnihu */ 'loan.VratitKnihu')}
        </KpButton>
    {/if}

    <!-- Migrate all loans -->
    {#if detailView.canMigrateFromUserToAny}
        <KpActionRequestButton isBlock
                               path="/api/loan-items/migrate"
                               requestMethod="post"
                               requestBody="{{source: user.id}}"
                               on:success={() => service.reloadRoute()}>

            Převést všechny výpůjčky
        </KpActionRequestButton>
    {/if}
</KpVerticalButtonsPanel>